import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Container = styled.div`
  padding: 24px;
  background: #f8f9fb;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 32px;
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 8px 0;
  }
  
  p {
    color: #64748b;
    margin: 0;
    font-size: 14px;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 16px;
`;

const RequestCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const PractitionerInfo = styled.div`
  flex: 1;
  
  .name {
    font-weight: 600;
    font-size: 16px;
    color: #1e293b;
    margin-bottom: 4px;
  }
  
  .email {
    color: #64748b;
    font-size: 13px;
  }
`;

const StatusBadge = styled.span`
  background: #fef08a;
  color: #854d0e;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
`;

const RequestMessage = styled.div`
  background: #f1f5f9;
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid #3b82f6;
  margin: 16px 0;
  font-size: 14px;
  color: #334155;
  line-height: 1.5;
`;

const Meta = styled.div`
  display: flex;
  gap: 24px;
  color: #64748b;
  font-size: 13px;
  margin-bottom: 16px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  
  .label {
    color: #475569;
    font-weight: 500;
    margin-bottom: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  
  ${props =>
    props.primary &&
    `
    background: #10b981;
    color: white;
    
    &:hover {
      background: #059669;
    }
    
    &:active {
      transform: scale(0.98);
    }
  `}
  
  ${props =>
    props.danger &&
    `
    background: #ef4444;
    color: white;
    
    &:hover {
      background: #dc2626;
    }
    
    &:active {
      transform: scale(0.98);
    }
  `}
  
  ${props =>
    props.secondary &&
    `
    background: #e2e8f0;
    color: #334155;
    
    &:hover {
      background: #cbd5e1;
    }
    
    &:active {
      transform: scale(0.98);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  
  .icon {
    font-size: 48px;
    margin-bottom: 12px;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #64748b;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }
  
  button {
    background: none;
    border: none;
    font-size: 24px;
    color: #64748b;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #1e293b;
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ChangeRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveModal, setApproveModal] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch change requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/change-requests`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching change requests:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch change requests');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Handle approve
  const handleApprove = async (practitionerId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API_URL}/admin/change-requests/${practitionerId}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success('Change request approved!');
      setApproveModal(null);
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reject
  const handleReject = async (practitionerId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API_URL}/admin/change-requests/${practitionerId}/reject`,
        { reason: rejectReason },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success('Change request rejected!');
      setRejectModal(null);
      setRejectReason('');
      fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <h1>🔄 Document Change Requests</h1>
        <p>Review and approve/reject document change requests from practitioners</p>
      </Header>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading change requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <EmptyState>
          <div className="icon">✅</div>
          <div className="title">All caught up!</div>
          <div className="description">No pending document change requests right now.</div>
        </EmptyState>
      ) : (
        <Grid>
          {requests.map((request) => (
            <RequestCard key={request.id}>
              <RequestHeader>
                <PractitionerInfo>
                  <div className="name">
                    {request.firstName} {request.lastName}
                  </div>
                  <div className="email">{request.email}</div>
                </PractitionerInfo>
                <StatusBadge>⏳ Pending</StatusBadge>
              </RequestHeader>

              <Meta>
                <MetaItem>
                  <span className="label">Requested</span>
                  <span>
                    {new Date(request.updatedAt).toLocaleDateString([], {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </MetaItem>
              </Meta>

              {request.changeRequestReason && (
                <RequestMessage>
                  <strong>Reason:</strong> {request.changeRequestReason}
                </RequestMessage>
              )}

              <ButtonGroup>
                <Button
                  primary
                  onClick={() => setApproveModal(request)}
                  disabled={actionLoading}
                >
                  ✓ Approve
                </Button>
                <Button
                  danger
                  onClick={() => setRejectModal(request)}
                  disabled={actionLoading}
                >
                  ✕ Reject
                </Button>
              </ButtonGroup>
            </RequestCard>
          ))}
        </Grid>
      )}

      {/* Approve Modal */}
      {approveModal && (
        <Modal onClick={() => setApproveModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Approve Change Request?</h2>
              <button onClick={() => setApproveModal(null)}>×</button>
            </ModalHeader>

            <p style={{ marginBottom: '24px', color: '#475569', fontSize: '14px' }}>
              <strong>
                {approveModal.firstName} {approveModal.lastName}
              </strong>{' '}
              will be able to update their documents (profile photo, license, CV).
            </p>

            <ButtonGroup>
              <Button
                secondary
                onClick={() => setApproveModal(null)}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                primary
                onClick={() => handleApprove(approveModal.id)}
                disabled={actionLoading}
              >
                {actionLoading ? '⏳ Approving...' : 'Approve'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <Modal onClick={() => setRejectModal(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Reject Change Request?</h2>
              <button onClick={() => setRejectModal(null)}>×</button>
            </ModalHeader>

            <p style={{ marginBottom: '16px', color: '#475569', fontSize: '14px' }}>
              <strong>
                {rejectModal.firstName} {rejectModal.lastName}
              </strong>{' '}
              will be notified that their document change request has been rejected.
            </p>

            <TextArea
              placeholder="Provide a reason for rejection (optional)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />

            <ButtonGroup style={{ marginTop: '24px' }}>
              <Button
                secondary
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                danger
                onClick={() => handleReject(rejectModal.id)}
                disabled={actionLoading}
              >
                {actionLoading ? '⏳ Rejecting...' : 'Reject'}
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ChangeRequests;
